import React from "react";
import Tab from "@mui/material/Tab";
import MuiTabs from "@mui/material/Tabs";
import { TabsProps as MuiTabsProps } from "@mui/material/Tabs/Tabs";
import Popper from "@mui/material/Popper";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";

import { OptionType } from "@covertree/types";
import { slugify } from "@covertree/utils";
import { colors } from "@covertree/theme/colors";

interface TabsProps
  extends Omit<
    MuiTabsProps,
    | "textColor"
    | "indicatorColor"
    | "value"
    | "onChange"
    | "className"
    | "TabIndicatorProps"
  > {
  disabled?: boolean;
  tabs: Omit<OptionType, "icon">[];
  value: number;
  onChange: (value: number) => void;
}

export const Tabs = ({
  tabs,
  value,
  onChange,
  disabled = false,
  ...props
}: TabsProps) => {
  const theme = useTheme();
  const [tooltipAnchor, setTooltipAnchor] = React.useState<HTMLElement | null>(
    null
  );
  const [activeTooltip, setActiveTooltip] = React.useState<string | null>(null);

  const handleChange = (
    _event: React.SyntheticEvent<Element, Event>,
    newValue: number
  ) => {
    onChange(newValue);
  };

  return (
    <>
      <MuiTabs
        value={value}
        onChange={handleChange}
        aria-label="tabs"
        textColor="secondary"
        indicatorColor="secondary"
        {...props}
        sx={{
          "& div.MuiTabs-scroller": {
            borderBottom: `1px solid ${colors.grey2}`,
          },
          "& div.MuiTabs-scrollButtons.Mui-disabled": {
            display: "none",
          },
          ...props.sx,
        }}
        TabIndicatorProps={{
          sx: {
            height: theme.spacing(0.25),
          },
        }}>
        {tabs.map(({ label, tooltip }, index) => {
          const itemId = `tab-${slugify(label)}`;
          return (
            <Tab
              key={itemId}
              sx={{
                minWidth: "unset",
                transition: "color 0.5s ease",
                "&.Mui-selected": {
                  color: "text.primary",
                },
                fontWeight: "400",
              }}
              label={label}
              value={index}
              disabled={disabled}
              id={itemId}
              data-testid={itemId}
              onMouseEnter={
                tooltip
                  ? (e) => {
                      setTooltipAnchor(e.currentTarget);
                      setActiveTooltip(tooltip);
                    }
                  : undefined
              }
              onMouseLeave={
                tooltip
                  ? () => {
                      setTooltipAnchor(null);
                      setActiveTooltip(null);
                    }
                  : undefined
              }
            />
          );
        })}
      </MuiTabs>
      <Popper
        open={Boolean(tooltipAnchor && activeTooltip)}
        anchorEl={tooltipAnchor}
        placement="bottom"
        sx={{ zIndex: 1500 }}
        modifiers={[{ name: "offset", options: { offset: [0, 6] } }]}>
        <Paper
          elevation={0}
          sx={{
            px: 1,
            py: 0.5,
            maxWidth: 260,
            backgroundColor: "rgba(97, 97, 97, 0.92)",
            borderRadius: "4px",
          }}>
          <Typography
            variant="caption"
            sx={{ color: "#fff", lineHeight: 1.4, display: "block" }}>
            {activeTooltip}
          </Typography>
        </Paper>
      </Popper>
    </>
  );
};
